import React, { Fragment } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { isDecimal } from 'validator'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'

import { useQuery } from 'components/graphql'
import { Form, Select, Number as NumberInput } from 'components/forms'
import { ExpandableBlocks, ContentBlock, ExpandableContext } from 'components/ContentBlock'
import { appConfig } from 'gql/query'
import queryPriceLevels from 'config/localServer/query/priceLevelsQuery'
import getDiscountGroupNames from 'config/localServer/query/getDiscountGroupNames'

import styles from './loyaltyCard.module.scss'

const LoyaltyCard = ({ data, formData = {}, mutations: { updateItem } }) => {
	const [t] = useTranslation()
	const { discount = [], cardSurchargePercent = [] } = formData
	const { data: discountNamesData, queryField } = useQuery(getDiscountGroupNames)
	const discountNamesArr = get(discountNamesData, queryField)
	const renderDiscounts = () => {
		const arr = [...discount]
		const numCols = 5
		const itemInCol = 4
		const out = []
		for(let i = 0; i <= numCols; i++) {
			const colContent = arr.splice(0, itemInCol).map((item, j) => {
				const ind = (itemInCol * i) + j + 1
				const groupName = get(discountNamesArr, [ind - 1])
				return (
					<NumberInput
						field={`discount[${ind - 1}]`}
						label={(groupName || t('product_section.strings.discount')) + ', %'}
						format='percent'
						precision={2}
						key={ind}
						placeholder='Rabatt'
						withControls={false}
						shrink />
				)
			})
			if(colContent.length) {
				out.push(
					<Col xs className='narrow-col' key={i}>
						{colContent}
					</Col>
				)
			}
		}
		return <Row>{out}</Row>
	}
	const optionsTypePoints = [
		{ value: 0, label: t('product_section.strings.no_bonus_points') },
		{ value: 1, label: t('product_section.strings.money') },
		{ value: 2, label: t('product_section.strings.item') }
	]
	const handleFormChange = vals => {
		const optimisticResponse = {
			...vals,
			points: {
				...vals.points,
				__typename: 'ProductPoints'
			}
		}
		updateItem(vals, { optimisticResponse })
	}
	const fieldsByBlocks = [
		['discount'],
		['cardSurchargePercent', 'cardSurchargeFrom', 'cardChargeAmount'],
		['points', 'pointsCost', 'pointsPriceLevel']
	]
	const handleSubmitErrors = setExpandedId => errors => {
		const errorField = Object.keys(errors)[0]
		const blockError = findIndex(fieldsByBlocks, arr => arr.find(field => field === errorField))
		if(blockError > -1) {
			setExpandedId(blockError.toString())
		}
	}
	const { data: configData, queryField: configQueryField } = useQuery(appConfig.pointsSystem)
	const { pointsSystemName, isActivePointsSystem } = get(configData, [configQueryField, 'cashRegisterGlobal'], {})
	const pointsSystemDisabled = isActivePointsSystem === false
	const titleForDisabled = pointsSystemDisabled ? t('product_section.strings.points_deactivated') : ''

	return(
		<ExpandableBlocks>
			<ExpandableContext.Consumer>
				{({ setExpandedId }) => (
					<Form
						initialValues={formData}
						onSubmit={handleFormChange}
						smartSubmit
						onSubmitFailure={handleSubmitErrors(setExpandedId)}
						className='flex-col-container'
					>
						{({ formState }) => {
							const points = get(formState, 'values.points') || get(formData, 'points', {})
							return(
								<Fragment>
									<ContentBlock startExpanded label={t('product_section.strings.discounts')} blockId='0'>
										{renderDiscounts()}
									</ContentBlock>
									<ContentBlock label={t('product_section.strings.prepaid_card')} blockId='1'>
										<Row>
											<Col xs>
												{cardSurchargePercent.map((item, i) => (
													<div key={i}>
														<NumberInput
															field={`cardSurchargePercent[${i}]`}
															label={`${t('product_section.fields.surcharge')} ${i + 1}, %`}
															withControls={false}
															format='percent'
															placeholder='Zuschlag'
															shrink
															inline />
														<NumberInput
															field={`cardSurchargeFrom[${i}]`}
															label={`${t('product_section.fields.ab_price')} ${i + 1}`}
															withControls={false}
															format='currency'
															placeholder='Betrag'
															shrink
															inline />
													</div>
												))}
											</Col>
											<Col xs>
												<NumberInput
													field='cardChargeAmount'
													label={t('product_section.fields.surcharge_amount')}
													format='currency'
													withControls={false} />
											</Col>
										</Row>
									</ContentBlock>
									<ContentBlock label={pointsSystemName || t('product_section.strings.points_system')} blockId='2'>
										<Row>
											<Col xs>
												<Select
													field='points.type'
													label={t('product_section.fields.collect')}
													options={optionsTypePoints}
													disabled={pointsSystemDisabled}
													simpleValue
													{...titleForDisabled && { title: titleForDisabled } } />
												<div {...points.type === 0 && { style: { display: 'none' } } } className={styles['container-expression']}>
													<NumberInput
														field='points.quantity'
														label={points.type === 1 ? t('product_section.strings.money') : t('common.fields.number')}
														withControls={false}
														{...points.type === 1 && { format: 'currency' }}
														{...points.type === 2 && {
															validate: val => isDecimal(val + '', { force_decimal: true }) || val < 0 ? t('validators.not_negative') : null,
															precision: false,
															validateOnChange: true
														}}
														shrink
														disabled={pointsSystemDisabled}
														{...titleForDisabled && { title: titleForDisabled } } />
													<span className={styles['equal-sign']}> = </span>
													<NumberInput
														field='points.points'
														label={t('product_section.fields.points')}
														withControls={false}
														shrink
													 	disabled={pointsSystemDisabled}
													 	{...titleForDisabled && { title: titleForDisabled } } />
												</div>
											</Col>
											<Col xs>
												<NumberInput
													field='pointsCost'
													label={t('product_section.fields.redeem_points')}
													withControls={false}
													disabled={pointsSystemDisabled}
													{...titleForDisabled && { title: titleForDisabled } } />
												<Select
													field='pointsPriceLevel'
													label={t('product_section.fields.price_level')}
													remoteOptions={{
														query: queryPriceLevels,
														variables: { onlyActive: false, defaultNames: true },
														modifyOptions: options => [{ value: 0, label: `<${t('product_section.strings.not_collect_points')}>` }]
															.concat(options.map((item, i) => ({ value: i + 1, label: item })))
													}}
													disabled={pointsSystemDisabled}
													{...titleForDisabled && { title: titleForDisabled } } />
											</Col>
										</Row>
									</ContentBlock>
								</Fragment>
							)
						}}
					</Form>
				)}
			</ExpandableContext.Consumer>
		</ExpandableBlocks>
	)
}

export default LoyaltyCard
